import { MAX_LISTED_REACTORS } from './reactions.constants';

export interface ReactionRecord {
  emoji: string;
  userId: string;
  createdAt: string;
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  reactedByMe: boolean;
  /** Everyone else who reacted with this emoji, in the order they reacted. */
  otherReactorNames: string[];
}

/**
 * Collapses reaction rows into one chip per emoji. Chips keep the order each emoji was first
 * used; reactors keep the order they reacted in.
 */
export function groupReactions(
  records: ReactionRecord[],
  currentUserId: string | undefined,
  resolveName: (userId: string) => string,
): ReactionSummary[] {
  const byEmoji = new Map<string, ReactionSummary>();
  const chronological = [...records].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  for (const { emoji, userId } of chronological) {
    const summary = byEmoji.get(emoji) ?? {
      emoji,
      count: 0,
      reactedByMe: false,
      otherReactorNames: [],
    };
    summary.count += 1;
    if (userId === currentUserId) {
      summary.reactedByMe = true;
    } else {
      summary.otherReactorNames.push(resolveName(userId));
    }
    byEmoji.set(emoji, summary);
  }

  return [...byEmoji.values()];
}

/**
 * Names the people behind a chip, the current user first:
 * "You", "You and Marty Lin", "You, Marty Lin, Ana Ruiz and 3 others".
 */
export function formatReactors({
  reactedByMe,
  otherReactorNames,
}: Pick<ReactionSummary, 'reactedByMe' | 'otherReactorNames'>): string {
  const names = reactedByMe ? ['You', ...otherReactorNames] : otherReactorNames;
  if (names.length <= 1) {
    return names[0] ?? '';
  }

  if (names.length <= MAX_LISTED_REACTORS) {
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }

  const hidden = names.length - MAX_LISTED_REACTORS;
  return `${names.slice(0, MAX_LISTED_REACTORS).join(', ')} and ${hidden} ${
    hidden === 1 ? 'other' : 'others'
  }`;
}
